import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { z } from "zod";
import { TFunction } from "i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form.tsx";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert.tsx";
import { AlertCircle, LoaderCircle } from "lucide-react";
import { ComponentProps, useEffect, useMemo, useState } from "react";
import {
  resetPasswordConfirm,
  resetPasswordValidateToken,
} from "@/api/authentication/resetPassword.ts";
import { Link, useLocation } from "react-router";
import PasswordInput from "@/components/PasswordInput.tsx";
import { setTitle } from "@/hooks/useHeader.ts";
import { useTranslation } from "@/i18n";

const createFormSchema = (t: TFunction) =>
  z
    .object({
      password: z.string().min(8, t("validation.passwordMinLength")),
      confirmPassword: z.string().min(8, t("validation.passwordMinLength")),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t("validation.passwordsDoNotMatch"),
      path: ["confirmPassword"],
    });

type FormValues = z.infer<ReturnType<typeof createFormSchema>>;

const ResetPassword = ({ className, ...props }: ComponentProps<"div">) => {
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [badToken, setBadToken] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();
  const formSchema = useMemo(() => createFormSchema(t), [t]);

  useEffect(() => {
    const checkToken = async () => {
      const queryParams = new URLSearchParams(location.search);
      const token = queryParams.get("token") || "";
      try {
        await resetPasswordValidateToken({ token });
      } catch (e) {
        console.log(e);
        setBadToken(true);
        setError(t("auth.resetPassword.invalidLink"));
      }
    };
    checkToken();
  }, [location.search, t]);

  useEffect(() => {
    setTitle(t("auth.resetPassword.title"));
  }, [t]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  const onSubmit = async (values: FormValues) => {
    setLoading(true);
    setError("");
    try {
      const queryParams = new URLSearchParams(location.search);
      const token = queryParams.get("token") || "";
      await resetPasswordConfirm({ token, ...values });
      setSuccess(true);
    } catch (e: unknown) {
      console.error(e);
      setError(t("auth.resetPassword.invalidLink"));
    }
    setLoading(false);
  };

  if (badToken) {
    return (
      <div className={cn("flex flex-col gap-6", className)} {...props}>
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">
              {t("auth.resetPassword.title")}
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-6">
            <Alert variant="destructive" className="mx-auto my-4 w-fit">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>{t("auth.resetPassword.failed")}</AlertTitle>
              <AlertDescription>
                {t("auth.resetPassword.invalidLink")}
              </AlertDescription>
            </Alert>
            <div>
              <div className="text-center text-sm">
                {t("auth.resetPassword.goTo")}{" "}
                <Link to="/sign-in" className="underline underline-offset-4">
                  {t("auth.resetPassword.signInLink")}
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">
            {t("auth.resetPassword.title")}
          </CardTitle>
          <CardDescription className="text-md">
            {success
              ? t("auth.resetPassword.success")
              : t("auth.resetPassword.description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="pb-6">
          {error && (
            <Alert variant="destructive" className="mx-auto my-4 w-fit">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>{t("auth.resetPassword.failed")}</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {success ? (
            <div>
              <div className="text-center text-sm">
                {t("auth.resetPassword.goTo")}{" "}
                <Link to="/sign-in" className="underline underline-offset-4">
                  {t("auth.resetPassword.signInLink")}
                </Link>
              </div>
            </div>
          ) : (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)}>
                <div className="grid gap-6">
                  <div className="grid gap-6">
                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel htmlFor="password">
                            {t("form.newPassword")}
                          </FormLabel>
                          <FormControl>
                            <PasswordInput
                              id="password"
                              {...field}
                              onChange={(e) => {
                                field.onChange(e);
                                setError(undefined);
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="confirmPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel htmlFor="confirmPassword">
                            {t("form.confirmPassword")}
                          </FormLabel>
                          <FormControl>
                            <PasswordInput
                              id="confirmPassword"
                              {...field}
                              onChange={(e) => {
                                field.onChange(e);
                                setError(undefined);
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button type="submit" className="w-full" disabled={loading}>
                      {t("auth.resetPassword.submit")}{" "}
                      {loading && <LoaderCircle className="animate-spin" />}
                    </Button>
                  </div>
                  <div className="text-center text-sm">
                    {t("auth.resetPassword.goTo")}{" "}
                    <Link
                      to="/sign-in"
                      className="underline underline-offset-4"
                    >
                      {t("auth.resetPassword.signInLink")}
                    </Link>
                  </div>
                </div>
              </form>
            </Form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
